import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { referralService } from 'components/api/referral';
import { Referral, ReferralParams, ReferralResponse } from 'types/referral';

interface ReferralState {
  data: Referral[];
  total: number;
  isLoading: boolean;
  params: ReferralParams;
  referredBy: {
    owner: {
      userId: number;
      username: string;
      email: string;
    };
  } | null;
}

const initialState: ReferralState = {
  data: [],
  total: 0,
  isLoading: false,
  params: {
    page: 1,
    limit: 10,
  },
  referredBy: null,
};

export const fetchUserReferrals = createAsyncThunk(
  'referral/fetchUserReferrals',
  async ({ userId, params }: { userId: string; params: ReferralParams }, { rejectWithValue }) => {
    try {
      const response = await referralService.getUserReferrals(userId, params);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to fetch referrals',
      );
    }
  },
);

const referralSlice = createSlice({
  name: 'referral',
  initialState,
  reducers: {
    setParams: (state, action: PayloadAction<Partial<ReferralParams>>) => {
      state.params = { ...state.params, ...action.payload };
    },
    clearData: (state) => {
      state.data = [];
      state.total = 0;
      state.referredBy = null;
      state.params = initialState.params;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserReferrals.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchUserReferrals.fulfilled, (state, action: PayloadAction<ReferralResponse>) => {
        state.isLoading = false;
        state.data = action.payload.data?.docs || [];
        state.total = action.payload.data?.totalDocs || 0;
        state.referredBy = action.payload.data?.referredBy || null;
      })
      .addCase(fetchUserReferrals.rejected, (state, action) => {
        state.isLoading = false;
      });
  },
});

export const { setParams, clearData } = referralSlice.actions;
export default referralSlice.reducer;
